import { useMemo } from "react";
import { useDirectoryTabularContext } from "@/providers/DirectoryTabularProvider";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBFieldDef";
import { ColumnDef, CellContext } from "@tanstack/react-table";
import { Button, createColumnWithMeta, renderCellValue } from "@theorgchartdev/storybook";
import {
  enumFieldType,
  enumObjClassNames,
  kEmployeeID,
  kEmployeePositionID,
  posObjClassFieldIds
} from "@coreapp/constants";
import { boxObjClassFieldIds } from "@coreapp/constants";
import {
  createAssignmentsColumn,
  createDesiredHoldersColumn,
  createParentPositionColumn,
  getOverflowText,
  isParentOrSubRow
} from "@/Utils/Table/PosMgmtUtils";
import { appstr } from "@coreapp/utils/appstr";
import {
  FormatFieldValue,
  getBlockedFieldDisplayValue,
  isBlockedField
} from "@coreapp/utils/FormatFieldValue";

export const useDirectoryTableColumns = (): ColumnDef<any>[] => {
  const nav = useAppNavigate();
  const { state, fetchColumnFilterOptions, getIsCustomChart } = useDirectoryTabularContext();

  const isCustomChart = getIsCustomChart();

  return useMemo(() => {
    const cols: ColumnDef<any>[] = [];
    const doc = GetDoc();
    const objDb = doc?.GetObjDB();

    if (!doc || !objDb) {
      return cols;
    }

    for (const fieldDef of state.columns as QObjDBFieldDef[]) {
      if (!fieldDef) {
        continue;
      }

      const fieldID = fieldDef.GetFieldID();
      const fieldName = fieldDef.GetFieldName();
      const fieldObjClassName = fieldDef.GetFieldClassName();
      const fieldType = fieldDef.GetFieldType();

      const commonDef: ColumnDef<any> = createColumnWithMeta<any, any>(
        {
          id: `${fieldID}_${fieldObjClassName}`,
          accessorFn: (row: any) => {
            let value;
            if (row.dataType === fieldObjClassName) {
              value = row[fieldName];
            } else if (row.subRows?.length === 1) {
              value = row.subRows[0][fieldName] ?? "";
            } else {
              return undefined;
            }
            if (isBlockedField(value)) return getBlockedFieldDisplayValue();
            return value ?? "";
          },
          header: () => getOverflowText(fieldDef.GetFieldLabel()),
          size: 175,
          cell: ({ row, getValue, column }: CellContext<any, string>) => {
            if (isParentOrSubRow(row) && row.original.dataType !== fieldObjClassName) {
              return "";
            }

            const value = getValue();
            let formattedValue: string;
            // Backend is responsible for date conversions and formatting. So avoid it here.
            if (fieldType === enumFieldType.date) {
              if (isBlockedField(value)) {
                formattedValue = getBlockedFieldDisplayValue();
              } else {
                formattedValue = value;
              }
            } else {
              formattedValue = FormatFieldValue(value, fieldDef);
            }
            return renderCellValue({
              row,
              value: formattedValue,
              column,
              renderValue: () => getOverflowText(formattedValue)
            });
          }
        },
        {
          dataType: fieldObjClassName ?? undefined,
          ...(isCustomChart
            ? {}
            : {
                filterOptions: {
                  fetchOptions: fetchColumnFilterOptions
                }
              })
        }
      );

      // For cases when this fieldID is a deprecated ID, we should convert the
      // system fieldDef IDs and the compare in the switch cases
      switch (fieldID) {
        case posObjClassFieldIds.POSITION_ID:
          cols.push({ ...commonDef, size: 150 });
          break;

        case posObjClassFieldIds.DESIRED_HOLDERS:
        case objDb.GetMappedFieldDefId(
          posObjClassFieldIds.DESIRED_HOLDERS,
          enumObjClassNames.position
        ):
          cols.push(createDesiredHoldersColumn(commonDef, objDb));
          break;

        case posObjClassFieldIds.PARENT_POSITION_ID:
        case objDb.GetMappedFieldDefId(
          posObjClassFieldIds.PARENT_POSITION_ID,
          enumObjClassNames.position
        ):
          cols.push(createParentPositionColumn(commonDef, objDb));
          break;

        case posObjClassFieldIds.POSITION_LOCATION:
        case objDb.GetMappedFieldDefId(
          posObjClassFieldIds.POSITION_LOCATION,
          enumObjClassNames.position
        ):
          cols.push({
            ...commonDef,
            size: 200
          });
          break;

        case posObjClassFieldIds.POSITION_TITLE:
        case objDb.GetMappedFieldDefId(
          posObjClassFieldIds.POSITION_TITLE,
          enumObjClassNames.position
        ):
          cols.push({
            ...commonDef,
            size: 300
          });
          break;

        case boxObjClassFieldIds.NAME: {
          const colDef = {
            ...createAssignmentsColumn(commonDef, fieldName),
            cell: ({ row, getValue, column }: CellContext<any, string>) => {
              if (row.getCanExpand()) {
                return `(${appstr("multiple")})`;
              }

              const value = getValue();

              if (isBlockedField(value) || value === getBlockedFieldDisplayValue()) {
                return getBlockedFieldDisplayValue();
              }

              const handleEmployeeClick = () => {
                const wsMgr = GetWSMgr();

                // Get employee data object from the row(handles both row and subrow)
                const canvasPanel = wsMgr?.fCanvasPanel;
                let rowData = row.original;
                // Get employee data object from the row(handles both row and subrow)
                // If the row has subRows, it's supposed to be a position row with single employee
                rowData = rowData.subRows?.length ? rowData.subRows[0] : rowData;

                if (!wsMgr || !canvasPanel || !rowData) {
                  return;
                }

                const boxClass = doc.GetBoxClass();
                const targetFieldName = boxClass
                  ?.GetFieldDefByID(kEmployeePositionID)
                  ?.GetFieldName();
                const highlightFieldName = boxClass?.GetFieldDefByID(kEmployeeID)?.GetFieldName();

                if (!targetFieldName || !highlightFieldName) {
                  return;
                }
                const targetID = rowData[targetFieldName];

                const highlightId = rowData[highlightFieldName];
                // Handle highlighting of holder boxes, navToBox would handle highlighting the position box
                canvasPanel.ClearInitialHighlight();
                canvasPanel.AddToInitialHighlight(highlightId);

                const cmdElement = {
                  targetID
                };
                wsMgr.DoNavToBox(cmdElement, true, undefined, false, true);

                ReRenderCanvas();
              };

              return renderCellValue({
                row,
                value,
                column,
                renderValue: value =>
                  value ? (
                    <Button modifier="link" size="small" onClick={handleEmployeeClick} color="info">
                      {value}
                    </Button>
                  ) : (
                    getOverflowText(value)
                  )
              });
            }
          };
          cols.push(colDef);
          break;
        }
        default:
          cols.push(commonDef);
      }
    }
    return cols;
  }, [state.columns, fetchColumnFilterOptions, isCustomChart, nav]);
};
